import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GraduationCap,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Search,
  Save,
  Users,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useInstitution } from '../../contexts/InstitutionContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast } from '../../Components/Feedback';
import { useApi } from '../../api/useApi';

export default function Bolsistas() {
  const { institutionId } = useInstitution();
  const { podeEditar } = usePermissao('/bolsistas');
  const navigate = useNavigate();
  const toast = useToast();
  const api = useApi();
  const queryClient = useQueryClient();

  // Mês de referência (padrão: mês atual 'YYYY-MM')
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos'); // 'todos' | 'atingiu' | 'abaixo'
  const [metaEditavel, setMetaEditavel] = useState(null);

  // Busca dados dos bolsistas
  const { data: resposta, isLoading } = useQuery({
    queryKey: ['bolsistas', institutionId, mes],
    queryFn: async () => {
      const res = await api.get(`/api/bolsistas?mes=${mes}`);
      return res?.data ?? res;
    },
    enabled: !!institutionId,
  });

  const bolsistas = useMemo(() => resposta?.bolsistas || [], [resposta?.bolsistas]);
  const resumo = resposta?.resumo || {
    total_bolsistas: 0,
    atingiram_meta: 0,
    abaixo_meta: 0,
    media_frequencia: 0,
  };
  const metaAtual = resposta?.meta_instituicao ?? 75;

  // Valor local do input de meta
  const metaInput = metaEditavel !== null ? metaEditavel : metaAtual;

  // Mutação para salvar a meta da instituição
  const salvarMetaMutation = useMutation({
    mutationFn: async (novaMeta) => {
      return await api.put('/api/bolsistas/meta', { meta: novaMeta });
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Meta de frequência atualizada com sucesso!');
      setMetaEditavel(null);
      queryClient.invalidateQueries({ queryKey: ['bolsistas', institutionId] });
    },
    onError: (err) => {
      toast.error(err.message || 'Erro ao atualizar meta de frequência.');
    },
  });

  // Funções de navegação de mês
  const navegarMes = (delta) => {
    const [ano, m] = mes.split('-').map(Number);
    const data = new Date(ano, m - 1 + delta, 1);
    setMes(`${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`);
  };

  // Formatação do nome do mês para exibição
  const nomeMesFormatado = useMemo(() => {
    const [ano, m] = mes.split('-').map(Number);
    const data = new Date(ano, m - 1, 1);
    return data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }, [mes]);

  // Lista filtrada
  const bolsistasFiltrados = useMemo(() => {
    return bolsistas.filter((b) => {
      const bateNome = b.nome.toLowerCase().includes(busca.toLowerCase());
      if (!bateNome) return false;

      if (filtroStatus === 'atingiu') return b.atingiu_meta;
      if (filtroStatus === 'abaixo') return !b.atingiu_meta;
      return true;
    });
  }, [bolsistas, busca, filtroStatus]);

  const handleSalvarMeta = (e) => {
    e.preventDefault();
    const val = Number(metaInput);
    if (isNaN(val) || val < 0 || val > 100) {
      toast.error('Informe um valor de meta entre 0 e 100%.');
      return;
    }
    salvarMetaMutation.mutate(val);
  };

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 p-4 md:p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <GraduationCap className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                Alunos Bolsistas
              </h1>
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                Acompanhamento mensal de frequência e metas do projeto
              </p>
            </div>
          </div>
        </div>

        {/* Controles de Mês e Meta da Instituição */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Navegador de Mês */}
          <div className="flex items-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-1 shadow-sm">
            <button
              onClick={() => navegarMes(-1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-600 dark:text-gray-300"
              title="Mês anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5 px-3">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-xs md:text-sm font-semibold capitalize text-gray-800 dark:text-gray-200">
                {nomeMesFormatado}
              </span>
            </div>
            <button
              onClick={() => navegarMes(1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-600 dark:text-gray-300"
              title="Próximo mês"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Ajuste da Meta Mínima da Instituição */}
          <form
            onSubmit={handleSalvarMeta}
            className="flex items-center gap-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl px-3 py-1.5 shadow-sm"
          >
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Meta Mínima:</span>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                value={metaInput}
                onChange={(e) => setMetaEditavel(e.target.value)}
                disabled={!podeEditar}
                className="w-12 text-center text-xs md:text-sm font-bold bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded"
              />
              <span className="text-xs font-bold text-gray-500 dark:text-gray-400">%</span>
            </div>
            {podeEditar && metaEditavel !== null && metaEditavel !== metaAtual && (
              <button
                type="submit"
                disabled={salvarMetaMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded-md text-xs transition-colors flex items-center gap-1"
                title="Salvar nova meta"
              >
                <Save className="w-3.5 h-3.5" />
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Total Bolsistas</span>
            <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {resumo.total_bolsistas}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs text-green-600 dark:text-green-400 font-medium">Atingiram a Meta</span>
            <p className="text-xl md:text-2xl font-bold text-green-700 dark:text-green-400">
              {resumo.atingiram_meta}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs text-red-600 dark:text-red-400 font-medium">Abaixo da Meta</span>
            <p className="text-xl md:text-2xl font-bold text-red-600 dark:text-red-400">
              {resumo.abaixo_meta}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Média de Frequência</span>
            <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {resumo.media_frequencia}%
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-900 p-3 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar bolsista por nome..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs md:text-sm bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div className="flex items-center gap-1.5 self-end sm:self-auto">
          <button
            onClick={() => setFiltroStatus('todos')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filtroStatus === 'todos'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            Todos ({bolsistas.length})
          </button>
          <button
            onClick={() => setFiltroStatus('atingiu')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filtroStatus === 'atingiu'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            Atingiram Meta ({resumo.atingiram_meta})
          </button>
          <button
            onClick={() => setFiltroStatus('abaixo')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filtroStatus === 'abaixo'
                ? 'bg-red-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            Abaixo da Meta ({resumo.abaixo_meta})
          </button>
        </div>
      </div>

      {/* Tabela de Bolsistas */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-gray-400 dark:text-gray-500">
            Carregando dados dos bolsistas...
          </div>
        ) : bolsistasFiltrados.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-400 dark:text-gray-500">
            {busca || filtroStatus !== 'todos'
              ? 'Nenhum bolsista encontrado com os filtros aplicados.'
              : 'Nenhum aluno bolsista cadastrado nesta instituição ainda.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs md:text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3 font-semibold">Aluno</th>
                  <th className="px-4 py-3 font-semibold">Turma / Turno</th>
                  <th className="px-4 py-3 font-semibold text-center">Presenças</th>
                  <th className="px-4 py-3 font-semibold text-center">Faltas</th>
                  <th className="px-4 py-3 font-semibold text-center">Dias Esperados</th>
                  <th className="px-4 py-3 font-semibold">Frequência (%)</th>
                  <th className="px-4 py-3 font-semibold text-center">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-800 dark:text-gray-200">
                {bolsistasFiltrados.map((aluno) => {
                  const atingiu = aluno.atingiu_meta;
                  const corBarra = atingiu ? 'bg-green-500' : 'bg-red-500';

                  return (
                    <tr
                      key={aluno.id}
                      className="hover:bg-gray-50/70 dark:hover:bg-gray-800/40 transition-colors"
                    >
                      {/* Foto e Nome */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {aluno.foto_url ? (
                            <img
                              src={aluno.foto_url}
                              alt={aluno.nome}
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center font-bold text-xs flex-shrink-0">
                              {aluno.nome.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <span className="font-semibold block truncate">{aluno.nome}</span>
                            {aluno.escola_atual && (
                              <span className="text-[11px] text-gray-400 block truncate">
                                {aluno.escola_atual}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Turma / Turno */}
                      <td className="px-4 py-3">
                        <span className="block font-medium">{aluno.turma || '-'}</span>
                        <span className="text-[11px] text-gray-400 block capitalize">
                          {aluno.turno || '-'}
                        </span>
                      </td>

                      {/* Presenças */}
                      <td className="px-4 py-3 text-center font-semibold text-green-600 dark:text-green-400">
                        {aluno.dias_presentes}
                      </td>

                      {/* Faltas */}
                      <td className="px-4 py-3 text-center font-semibold text-red-600 dark:text-red-400">
                        {aluno.dias_faltas}
                      </td>

                      {/* Dias Esperados */}
                      <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                        {aluno.dias_esperados}
                      </td>

                      {/* Barra de Frequência */}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold">{aluno.frequencia_pct}%</span>
                            <span className="text-[10px] text-gray-400">Meta: {metaAtual}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${corBarra}`}
                              style={{ width: `${aluno.frequencia_pct}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        {atingiu ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Atingiu
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300">
                            <AlertTriangle className="w-3.5 h-3.5" /> Abaixo
                          </span>
                        )}
                      </td>

                      {/* Ações */}
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/gerenciar-matriculas/aluno/${aluno.id}/editar`)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-lg"
                          title="Editar Cadastro do Aluno"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
